import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../config/tenantConfig';
import { useDarkMode } from '../DarkModeContext';
import GlassTopBar from '../components/GlassTopBar';
import CollapsibleSidebar from '../components/CollapsibleSidebar';
import LandingGuideAgent from '../components/LandingGuideAgent';
import DemoBanner from '../components/DemoBanner';
import Swal from 'sweetalert2';
import {
    LayoutDashboard,
    Briefcase,
    Wallet,
    Settings,
    PieChart,
    Sparkles,
    SlidersHorizontal,
} from 'lucide-react';

const InvestorLayout = ({ children }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { tenantName, theme, getTenantPath, tenantType } = useTenant();
    const { darkMode, toggleDarkMode } = useDarkMode();

    // Sidebar state — start closed on mobile
    const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
      typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
    );
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Calculate sidebar width for main content margin
    // On mobile (<1024px), sidebar overlays — no margin shift
    const isMobileScreen = typeof window !== 'undefined' && window.innerWidth < 1024;
    const sidebarWidth = isMobileScreen ? 0 : (isSidebarOpen ? (sidebarCollapsed ? 80 : 256) : 0);

    const navigation = [
        { label: 'Dashboard', path: getTenantPath('/dashboard'), icon: LayoutDashboard },
        { label: 'Dashboard Enhanced (Prototype)', path: getTenantPath('/dashboard-preview'), icon: Sparkles },
        { label: 'Marketplace', path: getTenantPath('/marketplace'), icon: Briefcase },
        { label: 'Portfolio', path: getTenantPath('/portfolio'), icon: PieChart },
        { label: 'Risk Matrix', path: getTenantPath('/risk-matrix'), icon: SlidersHorizontal },
        { label: 'Wallet', path: getTenantPath('/wallet'), icon: Wallet },
        { label: 'Settings', path: getTenantPath('/settings'), icon: Settings },
    ];

    // Add colors to navigation items
    const navigationWithColors = navigation.map(item => ({
        ...item,
        color: theme.primary
    }));

    const handleLogout = async () => {
        const { isConfirmed } = await Swal.fire({
            title: 'Confirm exit',
            text: 'Are you sure you want to leave the investor workspace?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, exit',
            cancelButtonText: 'Stay',
            reverseButtons: true,
            background: darkMode ? '#1F2937' : '#fff',
            color: darkMode ? '#F9FAFB' : '#111827',
            confirmButtonColor: theme.primary
        });

        if (isConfirmed) {
            logout();
            navigate('/haliflow-landing');
        }
    };

    const handleSidebarToggle = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleNavigate = (path) => {
        navigate(path);
        if (window.innerWidth < 1024) {
            setIsSidebarOpen(false);
        }
    };

    const displayRole = user?.category || 'Investor';

    return (
        <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {/* Top Bar */}
            <GlassTopBar
                logoSrc={theme.logo || `${process.env.PUBLIC_URL || ''}/logo.svg`}
                brandName={theme.brandName || tenantName}
                brandSubtitle={tenantType === 'halifund' ? 'Funder Portal' : 'Investor Portal'}
                displayRole={displayRole}
                userEmail={user?.email || ''}
                darkMode={darkMode}
                toggleDarkMode={toggleDarkMode}
                isSidebarCollapsed={!isSidebarOpen}
                onSidebarToggle={handleSidebarToggle}
                onBrandClick={() => navigate(getTenantPath('/dashboard'))}
                onLogout={handleLogout}
                borderColor={theme.primary + '20'}
            />

            <div className="flex">
                {/* Sidebar */}
                <CollapsibleSidebar
                    className={!isSidebarOpen ? 'sidebar-hidden' : ''}
                    style={{ top: '96px' }} // Position below the 96px (h-24) header
                    logoSrc={theme.logo || `${process.env.PUBLIC_URL || ''}/logo.svg`}
                    brandName={tenantName || "Haliflow"}
                    subtitle={tenantType === 'halifund' ? 'Funder' : 'Investor'}
                    viewBadge={tenantType === 'halifund' ? 'Funder View' : 'Investor View'}
                    menuItems={navigationWithColors}
                    activePath={location.pathname}
                    onNavigate={handleNavigate}
                    onToggle={(collapsed) => setSidebarCollapsed(collapsed)}
                    primaryColor={theme.primary}
                    darkMode={darkMode}
                />

                {/* Main Content */}
                <main
                    className="flex-1 overflow-auto transition-all duration-300 ease-in-out"
                    style={{ marginLeft: sidebarWidth }}
                >
                    {/* Persistent, non-dismissible sandbox banner (demo tenant only) */}
                    <DemoBanner />
                    <div className="p-6 lg:p-8">
                        {/* Optional: Wrap in glass panel if desired, matching PlatformLayout */}
                        {/* <div className="glass-surface glass-panel rounded-[28px] p-6 lg:p-8 shadow-[0_35px_120px_rgba(15,23,42,0.15)]"> */}
                        {children}
                        {/* </div> */}
                    </div>
                </main>
            </div>

            {/* Mobile Overlay */}
            {isSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            <LandingGuideAgent darkMode={darkMode} theme="default" position="right" />
        </div>
    );
};

export default InvestorLayout;
